import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Plus, Trash2 } from "lucide-react";

const PRESETS = [
  { name: "Small · 40 × 25 mm", width: 40, height: 25 },
  { name: "Standard · 50 × 30 mm", width: 50, height: 30 },
  { name: "Large · 60 × 40 mm", width: 60, height: 40 },
];
const LIMITS = { width: [15, 100], height: [15, 100], copies: [1, 100] };

const productCode = (product) => String(product?.barcode || product?.code || "").trim();
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[char]));

export default function QrLabelSettings({ form, set }) {
  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState("");
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsRetry, setProductsRetry] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(0);
  const qrCanvasRef = useRef(null);
  const profiles = Array.isArray(form?.qr_label_printers) ? form.qr_label_printers : [];

  useEffect(() => {
    let active = true;
    setProductsLoading(true);
    setProductsError("");
    http.get("/products")
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data)) {
          setProductsError("The products response was not in the expected format.");
          return;
        }
        setProducts(data);
      })
      .catch((error) => { if (active) setProductsError(requestErrorMessage(error, "load products")); })
      .finally(() => { if (active) setProductsLoading(false); });
    return () => { active = false; };
  }, [productsRetry]);

  const selected = products.find((product) => String(product.id) === String(selectedProductId));
  const code = productCode(selected);
  const profile = profiles[selectedProfile];
  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) =>
      `${product.name || ""} ${product.code || ""} ${product.barcode || ""}`.toLowerCase().includes(query)
    );
  }, [products, search]);
  const width = Number(profile?.width_mm);
  const height = Number(profile?.height_mm);
  const copies = Number(profile?.copies);
  const validProfile = profile && Number.isFinite(width) && width >= LIMITS.width[0] && width <= LIMITS.width[1]
    && Number.isFinite(height) && height >= LIMITS.height[0] && height <= LIMITS.height[1]
    && Number.isInteger(copies) && copies >= LIMITS.copies[0] && copies <= LIMITS.copies[1];

  const updateProfile = (index, key, value) => {
    const next = profiles.map((item) => ({ ...item }));
    next[index] = { ...next[index], [key]: value };
    set("qr_label_printers", next);
  };
  const addProfile = () => {
    const next = [...profiles, {
      name: `QR Printer ${profiles.length + 1}`,
      width_mm: 50,
      height_mm: 30,
      copies: 1,
    }];
    set("qr_label_printers", next);
    setSelectedProfile(next.length - 1);
  };
  const removeProfile = (index) => {
    const next = profiles.filter((_, itemIndex) => itemIndex !== index);
    set("qr_label_printers", next);
    setSelectedProfile(Math.max(0, Math.min(selectedProfile, next.length - 1)));
  };
  const applyPreset = (preset) => {
    if (!profile) return;
    const next = profiles.map((item) => ({ ...item }));
    next[selectedProfile] = { ...next[selectedProfile], width_mm: preset.width, height_mm: preset.height };
    set("qr_label_printers", next);
  };

  const printLabels = () => {
    if (!selected || !code || !validProfile) return;
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    let qrImage;
    try {
      qrImage = canvas.toDataURL("image/png");
    } catch {
      return;
    }
    const popup = window.open("", "_blank");
    if (!popup) {
      window.alert("The print window was blocked. Allow pop-ups for this site, then try again.");
      return;
    }
    popup.opener = null;
    const safeName = escapeHtml(selected.name);
    const safeCode = escapeHtml(code);
    const safePrinterName = escapeHtml(profile.name || "QR label");
    const label = `<section class="label"><img class="qr" src="${qrImage}" alt="QR code"><div class="name">${safeName}</div><div class="code">${safeCode}</div></section>`;
    const pages = Array.from({ length: copies }, () => label).join("");
    popup.document.open();
    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${safePrinterName}</title><style>
      @page { size: ${width}mm ${height}mm; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #111; }
      .label { width: ${width}mm; height: ${height}mm; padding: 2mm; display: flex; flex-direction: column; align-items: center; justify-content: center; overflow: hidden; page-break-after: always; break-after: page; }
      .qr { width: min(58%, ${Math.max(8, height - 12)}mm); max-height: ${Math.max(8, height - 12)}mm; object-fit: contain; }
      .name { max-width: 100%; margin-top: 1mm; font-size: 9pt; font-weight: 600; text-align: center; overflow-wrap: anywhere; }
      .code { max-width: 100%; margin-top: .5mm; font-family: monospace; font-size: 8pt; text-align: center; overflow-wrap: anywhere; }
      @media print { .label:last-child { page-break-after: auto; break-after: auto; } }
    </style></head><body>${pages}<script>window.onload = function () { window.focus(); window.print(); };</script></body></html>`);
    popup.document.close();
  };

  const printDisabledReason = !selected
    ? "Choose a product to preview and print its QR label."
    : !code
      ? "Printing is disabled because this product has no barcode or product code. Add one in Products so POS scanning can resolve the QR value."
      : !profile
        ? "Add a printer profile to print labels."
        : !validProfile
          ? "Enter valid label dimensions (15–100 mm) and copies (1–100)."
          : "";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">QR Label Printers</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Create physical product labels with QR codes that contain the product barcode or code for POS scanning. QR codes only; no 1D barcodes are printed.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium">Printer profiles</h3>
              <p className="text-xs text-muted-foreground">Set a label size and copy count for each printer.</p>
            </div>
            <Button type="button" size="sm" onClick={addProfile}><Plus className="h-4 w-4" /> Add profile</Button>
          </div>
          {!profiles.length && <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No printer profiles yet. Add a profile to configure label printing.</p>}
          {profiles.map((item, index) => (
            <div key={item.id || index} className={`rounded-lg border p-3 ${selectedProfile === index ? "border-primary/50 bg-primary/[0.03]" : ""}`}>
              <div className="mb-3 flex items-center gap-2">
                <button type="button" onClick={() => setSelectedProfile(index)} className="min-w-0 flex-1 text-left font-medium">
                  {item.name || `QR Printer ${index + 1}`}
                  {selectedProfile === index && <span className="ml-2 text-xs font-normal text-muted-foreground">Selected</span>}
                </button>
                <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${item.name || "printer profile"}`} onClick={() => removeProfile(index)}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </div>
              {selectedProfile === index && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div><Label htmlFor={`qr-profile-name-${index}`}>Printer name</Label><Input id={`qr-profile-name-${index}`} className="mt-1" value={item.name || ""} onChange={(event) => updateProfile(index, "name", event.target.value)} placeholder="e.g. Shelf label printer" /></div>
                  <div><Label htmlFor={`qr-width-${index}`}>Label width (mm)</Label><Input id={`qr-width-${index}`} className="mt-1" type="number" min={15} max={100} step={1} value={item.width_mm ?? ""} onChange={(event) => updateProfile(index, "width_mm", event.target.value)} onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isFinite(value)) updateProfile(index, "width_mm", Math.min(100, Math.max(15, value)));
                  }} /></div>
                  <div><Label htmlFor={`qr-height-${index}`}>Label height (mm)</Label><Input id={`qr-height-${index}`} className="mt-1" type="number" min={15} max={100} step={1} value={item.height_mm ?? ""} onChange={(event) => updateProfile(index, "height_mm", event.target.value)} onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isFinite(value)) updateProfile(index, "height_mm", Math.min(100, Math.max(15, value)));
                  }} /></div>
                  <div><Label htmlFor={`qr-copies-${index}`}>Copies</Label><Input id={`qr-copies-${index}`} className="mt-1" type="number" min={1} max={100} step={1} value={item.copies ?? ""} onChange={(event) => updateProfile(index, "copies", event.target.value)} onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isFinite(value)) updateProfile(index, "copies", Math.min(100, Math.max(1, Math.round(value))));
                  }} /></div>
                  <div className="sm:col-span-2 lg:col-span-4">
                    <span className="text-xs font-medium text-muted-foreground">Quick sizes</span>
                    <div className="mt-1.5 flex flex-wrap gap-2">{PRESETS.map((preset) => <Button key={preset.name} type="button" variant="outline" size="sm" onClick={() => applyPreset(preset)}>{preset.name}</Button>)}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,340px)]">
        <Card>
          <CardContent className="space-y-3 p-4">
            <div><Label htmlFor="qr-product-search">Find product</Label><Input id="qr-product-search" className="mt-1" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name, product code, or barcode" /></div>
            <div>
              <Label htmlFor="qr-product">Product</Label>
              <select id="qr-product" value={selectedProductId} onChange={(event) => setSelectedProductId(event.target.value)} className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                <option value="">Select a product</option>
                {filteredProducts.map((product) => <option key={product.id} value={product.id}>{product.name} · {product.barcode || product.code || "No scan code"}</option>)}
              </select>
            </div>
            {productsError && <div role="alert" className="text-sm text-rose-600">{productsError} <Button type="button" variant="outline" size="sm" onClick={() => setProductsRetry((attempt) => attempt + 1)}>Retry</Button></div>}
            {!productsError && productsLoading && <p className="text-sm text-muted-foreground">Loading products…</p>}
            {!productsError && !productsLoading && products.length === 0 && <p className="text-sm text-muted-foreground">No products found.</p>}
            {selected && <p className="text-sm text-muted-foreground">QR value: <span className="font-mono text-foreground">{code || "No product code or barcode"}</span></p>}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex min-h-[230px] flex-col items-center justify-center p-4 text-center">
            <h3 className="mb-3 self-start font-medium">Label preview</h3>
            {selected && code ? (
              <div className="flex max-w-full flex-col items-center justify-center overflow-hidden border border-dashed border-slate-400 bg-white p-1 text-slate-900" style={{ width: `${Math.min(width || 50, 100)}mm`, height: `${Math.min(height || 30, 100)}mm`, maxWidth: "100%" }}>
                <QRCodeCanvas ref={qrCanvasRef} value={code} size={512} level="M" includeMargin style={{ width: "58%", maxHeight: `${Math.max(8, (height || 30) - 11)}mm`, height: "auto" }} />
                <div className="mt-1 max-w-full break-words text-[10px] font-semibold">{selected.name}</div>
                <div className="max-w-full break-all font-mono text-[9px]">{code}</div>
              </div>
            ) : <p className="text-sm text-muted-foreground">{printDisabledReason}</p>}
            {selected && code && printDisabledReason && <p className="mt-2 text-xs text-amber-700">{printDisabledReason}</p>}
            <Button type="button" variant="print" className="mt-4" onClick={printLabels} disabled={!selected || !code || !validProfile}>
              <Printer className="h-4 w-4" /> Print labels{validProfile ? ` · ${copies} ${copies === 1 ? "copy" : "copies"}` : ""}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}